import makeActionCreator from 'src/lib/makeActionCreator';
import { State } from 'src/redux/reducers/RootReducer';
import {
  activateFundraisingData,
  deleteFundraisingData,
  getFundraisingData, getFundraisingListData,
  postFundraisingInfoData,
  putFundraisingAmbassadorsData,
  putFundraisingAssetsData,
  putFundraisingInfoData, putFundraisingNotificationsData,
  putFundraisingProductsData, putFundraisingTicketsData,
} from 'src/apis/FundraisingAPI';
import { setAlert } from 'src/redux/actions/alertActions';
import { ALERT_TYPE } from 'src/constants/AlertType';

import Debug from 'src/lib/Debug';
import {
  FundraisingAmbassadors,
  FundraisingAssets,
  FundraisingInfo,
  FundraisingMatchingInfo, FundraisingMessage,
  FundraisingModel,
  FundraisingNotifications,
  FundraisingPayload,
  FundraisingProductsInfo,
  FundraisingTicketingInfo,
} from 'src/models/FundraisingModel';
import {
  AMBASSADORS_KEYS,
  FUNDRAISING_INFO_KEYS,
  MATCHING_INFO_KEYS,
  NOTIFICATIONS_KEYS,
  pick,
  PRODUCTS_KEYS,
} from 'src/constants/Fields';
import { setError } from 'src/redux/actions/errorsActions';
import { FORM, FORM_STATUS } from 'src/constants/Form';
import { setFormStatus } from 'src/redux/actions/formActions';
import { getEntitiesAction } from 'src/redux/actions/querybuilderActions';
import { ENTITIES } from 'src/models/QuerybuilderModel';
import { isTextAssetType } from 'src/constants/FundraisingTypes';
import { toOrgTimezone } from 'src/lib/DateHelper';
import moment from 'moment-timezone-all';

export const ADD_FUNDRAISING = 'ADD_FUNDRAISING';
export const SET_FUNDRAISINGS = 'SET_FUNDRAISINGS';
export const REMOVE_FUNDRAISING = 'REMOVE_FUNDRAISING';

export const addFundraising = makeActionCreator(ADD_FUNDRAISING);
export const setFundraisings = makeActionCreator(SET_FUNDRAISINGS);
export const removeFundraising = makeActionCreator(REMOVE_FUNDRAISING);

export const normalizeFundraisingData = (rawData: any, timezone?: string) => {
  const data:any = {
    ...rawData,
    matching_info: { ...rawData.matching_info },
    matching_assets: { ...rawData.matching_assets },
  };
  const isTextAsset = isTextAssetType(rawData.type);
  const fundraisingInfo =
    pick<FundraisingInfo, typeof FUNDRAISING_INFO_KEYS>(data, FUNDRAISING_INFO_KEYS) || null;
  const products =
    pick<FundraisingProductsInfo, typeof PRODUCTS_KEYS>(data, PRODUCTS_KEYS) || null;

  const ticketingInfo: FundraisingTicketingInfo | null = data.ticketing_info
    ? ({
      ...data.ticketing_info,
      event_date: data.ticketing_info.event_date
        ? moment(data.ticketing_info.event_date)
        : undefined,
    } as FundraisingTicketingInfo)
    : null;

  if (timezone && fundraisingInfo) {
    fundraisingInfo.start_date = toOrgTimezone(data.start_date, timezone);
    fundraisingInfo.end_date = toOrgTimezone(data.end_date, timezone);
    fundraisingInfo.countdown_end_date = toOrgTimezone(data.countdown_end_date, timezone);
  }
  return {
    fundraisingInfo,
    matchingInfo:
    // eslint-disable-next-line max-len
      pick<FundraisingMatchingInfo, typeof MATCHING_INFO_KEYS>(data.matching_info, MATCHING_INFO_KEYS) || null,
    products,
    ticketingInfo,
    ambassadors:
      pick<FundraisingAmbassadors, typeof AMBASSADORS_KEYS>(data, AMBASSADORS_KEYS) || null,
    notifications:
      pick<FundraisingNotifications, typeof NOTIFICATIONS_KEYS>(data, NOTIFICATIONS_KEYS) || null,
    assets: isTextAsset
      ? data.media_text
      : (data.matching_assets as FundraisingAssets) || null,
  };
};

type Callback = (newState: FundraisingModel) => void;

export const getFundraisingListAction: () => ThunkedAction<State> =
  () => async (dispatch: any, getState: any) => {
    try {
      const organisation = getState().currentOrganisation.id;
      const timezone = getState().data.timezones[getState().organisation.timezoneId];
      const response = await getFundraisingListData(organisation);
      if (response.success && (!!response.data.list)) {
        const fundraisings: Record<string, any> = {};
        response.data.list.forEach((data: any) => {
          const fundraising = normalizeFundraisingData({
            ...data,
            matching_info: { ...data.matching_info },
          } as FundraisingPayload, timezone);
          if (fundraising.fundraisingInfo?.id) {
            fundraisings[fundraising.fundraisingInfo.id] = fundraising;
          }
        });
        dispatch(setFundraisings(fundraisings));
      }
    } catch (e) {
      Debug.errorLog(e);
    }
  };
export const getFundraisingAction : (id: string | number) => ThunkedAction<State> =
  (id: string | number) => async (dispatch: any, getState: any) => {
    try {
      dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.loading }));
      const organisation = getState().currentOrganisation.id;
      const timezone = getState().data.timezones[getState().organisation.timezoneId];
      const response = await getFundraisingData(organisation, id);
      if (response.success) {
        const data = {
          ...response.data,
          matching_info: { ...response.data.matching_info },
        };
        const fundraising = normalizeFundraisingData(data as FundraisingPayload, timezone);
        dispatch(addFundraising({ id, fundraising }));
        dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.success }));
      } else {
        dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.error }));
      }
    } catch (e) {
      Debug.errorLog(e);
      dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.error }));
    }
  };

export const deleteFundraisingAction : (id: string) => ThunkedAction<State> =
  (id: string) => async (dispatch: any, getState: any) => {
    try {
      const organisation = getState().currentOrganisation.id;
      const response = await deleteFundraisingData(organisation, id);
      if (response.success) {
        dispatch(removeFundraising(id));
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          message: 'Fundraising successfully deleted',
        }));
        dispatch(getEntitiesAction(ENTITIES.fundraisings));
        return;
      }
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error deleting fundraising',
      }));
    } catch (e) {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error deleting fundraising',
      }));
      Debug.errorLog(e);
    }
  };

export const activateFundraisingAction : (id: string, state: boolean) => ThunkedAction<State> =
  (id: string, state: boolean) => async (dispatch: any, getState: any) => {
    try {
      const organisation = getState().currentOrganisation.id;
      const response = await deleteFundraisingData(organisation, id);
      if (response.success) {
        dispatch(activateFundraisingData(organisation, id, state));
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          message: 'Fundraising successfully activated/deactivated',
        }));
        return;
      }
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error activating/deactivating fundraising',
      }));
    } catch (e) {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error activating/deactivating fundraising',
      }));
      Debug.errorLog(e);
    }
  };

export const postFundraisingInfoAction: (
  data: FundraisingModel, callback?: Callback) => ThunkedAction<State> =
  (data: FundraisingModel, callback?: Callback) => async (dispatch: any, getState: any) => {
    try {
      const organisation = getState().currentOrganisation.id;
      const timezone = getState().data.timezones[getState().organisation.timezoneId];
      const payload = {
        ...data.fundraisingInfo,
        matching_info: data.matchingInfo,
      } as FundraisingInfo | FundraisingMatchingInfo;
      const response = await postFundraisingInfoData(organisation, payload);
      dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.success }));
      if (response.success) {
        const { id } = response.data;
        const fundraising = normalizeFundraisingData(response.data as FundraisingPayload, timezone);
        dispatch(addFundraising({ id, fundraising }));
        dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.success }));
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          message: 'Fundraising successfully saved',
        }));
        if (callback) {
          callback(fundraising as FundraisingModel);
        }
        dispatch(getEntitiesAction(ENTITIES.fundraisings));
        return;
      }
      if (response.status === 400) {
        dispatch(setError({ [FORM.fundraising]: response.errors.fields }));
        dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.error }));
        return;
      }
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error saving fundraising',
      }));
      dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.error }));
    } catch (e) { /* Log the error here */
      Debug.errorLog(e);
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error saving fundraising',
      }));
    }
  };

export const putFundraisingInfoAction: (
  id: number, data: FundraisingModel, callback?: Callback
) => ThunkedAction<State> =
  (
    id: number, data: FundraisingModel, callback?: Callback,
  ) => async (dispatch: any, getState: any) => {
    try {
      const organisation = getState().currentOrganisation.id;
      const timezone = getState().data.timezones[getState().organisation.timezoneId];
      const payload = {
        ...data.fundraisingInfo,
        matching_info: data.matchingInfo,
      } as FundraisingInfo | FundraisingMatchingInfo;
      dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.processing }));
      const response = await putFundraisingInfoData(organisation, id, payload);
      if (response.success) {
        const fundraising = normalizeFundraisingData(response.data as FundraisingPayload, timezone);
        dispatch(addFundraising({ id, fundraising }));
        dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.success }));
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          message: 'Fundraising successfully saved',
        }));
        if (callback) {
          callback(fundraising as FundraisingModel);
        }
        dispatch(getEntitiesAction(ENTITIES.fundraisings));
        return;
      }
      if (response.status === 400) {
        dispatch(setError({ [FORM.fundraising]: response.errors.fields }));
        dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.error }));
      }
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error saving fundraising',
      }));
      dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.error }));
    } catch (e) { /* Log the error here */
      Debug.errorLog(e);
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error saving fundraising',
      }));
    }
  };

export const putFundraisingProductsAction: (
  id: number, data: FundraisingModel, callback?: Callback
) => ThunkedAction<State> =
  (
    id: number, data: FundraisingModel, callback?: Callback,
  ) => async (dispatch: any, getState: any) => {
    try {
      const organisation = getState().currentOrganisation.id;
      const timezone = getState().data.timezones[getState().organisation.timezoneId];
      const payload = {
        ...data.products,
      } as FundraisingProductsInfo;
      const response = await putFundraisingProductsData(organisation, id, payload);
      if (response.success) {
        const fundraising = normalizeFundraisingData(response.data as FundraisingPayload, timezone);
        dispatch(addFundraising({ id, fundraising }));
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          message: 'Fundraising successfully saved',
        }));
        if (callback) {
          callback(fundraising as FundraisingModel);
        }
        dispatch(getEntitiesAction(ENTITIES.fundraisings));
        return;
      }
      if (response.status === 400) {
        dispatch(setError({ [FORM.fundraising]: response.errors.fields }));
        dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.error }));
      }
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error saving fundraising',
      }));
    } catch (e) { /* Log the error here */
      Debug.errorLog(e);
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error saving fundraising',
      }));
    }
  };

export const putFundraisingTicketsAction: (
  id: number, data: FundraisingModel, callback?: Callback
) => ThunkedAction<State> =
  (
    id: number, data: FundraisingModel, callback?: Callback,
  ) => async (dispatch: any, getState: any) => {
    try {
      const organisation = getState().currentOrganisation.id;
      const timezone = getState().data.timezones[getState().organisation.timezoneId];
      const { ticketingInfo } = data;
      const payload = {
        title: ticketingInfo?.title ?? null,
        event_date: ticketingInfo?.event_date
          ? ticketingInfo.event_date.format('YYYY-MM-DD')
          : null,
        location_name: ticketingInfo?.location_name ?? null,
        location_address: ticketingInfo?.location_address ?? null,
        contact_name: ticketingInfo?.contact_name ?? null,
        contact_phone: ticketingInfo?.contact_phone ?? null,
        contact_email: ticketingInfo?.contact_email ?? null,
        ticket_message: ticketingInfo?.ticket_message ?? null,
        ticket_send_mode: ticketingInfo?.ticket_send_mode ?? null,
      };
      const response = await putFundraisingTicketsData(organisation, id, payload);
      if (response.success) {
        const fundraising = normalizeFundraisingData(response.data as FundraisingPayload, timezone);
        dispatch(addFundraising({ id, fundraising }));
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          message: 'Fundraising successfully saved',
        }));
        if (callback) {
          callback(fundraising as FundraisingModel);
        }
        dispatch(getEntitiesAction(ENTITIES.fundraisings));
        return;
      }
      if (response.status === 400) {
        dispatch(setError({ [FORM.fundraising]: response.errors.fields }));
        dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.error }));
      }
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error saving fundraising',
      }));
    } catch (e) {
      Debug.errorLog(e);
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error saving fundraising',
      }));
    }
  };

export const putFundraisingAmbassadorsAction: (
  id: number, data: FundraisingModel, callback?: Callback
) => ThunkedAction<State> =
  (
    id: number, data: FundraisingModel, callback?: Callback,
  ) => async (dispatch: any, getState: any) => {
    try {
      const organisation = getState().currentOrganisation.id;
      const timezone = getState().data.timezones[getState().organisation.timezoneId];
      const payload = {
        ...data.ambassadors,
      };
      const response = await putFundraisingAmbassadorsData(organisation, id, payload);
      if (response.success) {
        const fundraising = normalizeFundraisingData(response.data as FundraisingPayload, timezone);
        dispatch(addFundraising({ id, fundraising }));
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          message: 'Fundraising successfully saved',
        }));
        if (callback) {
          callback(fundraising as FundraisingModel);
        }
        dispatch(getEntitiesAction(ENTITIES.fundraisings));
        return;
      }
      if (response.status === 400) {
        dispatch(setError({ [FORM.fundraising]: response.errors.fields }));
        dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.error }));
      }
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error saving fundraising',
      }));
    } catch (e) { /* Log the error here */
      Debug.errorLog(e);
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error saving fundraising',
      }));
    }
  };

export const putFundraisingNotificationsAction: (
  id: number, data: FundraisingModel, callback?: Callback
) => ThunkedAction<State> =
  (
    id: number, data: FundraisingModel, callback?: Callback,
  ) => async (dispatch: any, getState: any) => {
    try {
      const organisation = getState().currentOrganisation.id;
      const timezone = getState().data.timezones[getState().organisation.timezoneId];
      const payload = {
        ...data.notifications,
      } as FundraisingNotifications;
      const response = await putFundraisingNotificationsData(organisation, id, payload);
      if (response.success) {
        const fundraising = normalizeFundraisingData(response.data as FundraisingPayload, timezone);
        dispatch(addFundraising({ id, fundraising }));
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          message: 'Fundraising successfully saved',
        }));
        if (callback) {
          callback(fundraising as FundraisingModel);
        }
        dispatch(getEntitiesAction(ENTITIES.fundraisings));
        return;
      }
      if (response.status === 400) {
        dispatch(setError({ [FORM.fundraising]: response.errors.fields }));
        dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.error }));
      }
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error saving fundraising',
      }));
    } catch (e) { /* Log the error here */
      Debug.errorLog(e);
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error saving fundraising',
      }));
    }
  };

export const putFundraisingAssetsAction: (
  id: number, data: FundraisingModel, callback?: Callback
) => ThunkedAction<State> =
  (
    id: number, data: FundraisingModel, callback?: Callback,
  ) => async (dispatch: any, getState: any) => {
    try {
      const organisation = getState().currentOrganisation.id;
      const timezone = getState().data.timezones[getState().organisation.timezoneId];
      let payload: FundraisingAssets | FundraisingMessage;
      if (typeof data.assets === 'string') {
        payload = {
          media_text: data.assets,
        };
      } else {
        payload = {
          ...data.assets,
        } as FundraisingAssets;
      }
      const response = await putFundraisingAssetsData(organisation, id, payload);
      if (response.success) {
        const fundraising = normalizeFundraisingData(response.data as FundraisingPayload, timezone);
        dispatch(addFundraising({ id, fundraising }));
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          message: 'Fundraising successfully saved',
        }));
        if (callback) {
          callback(fundraising as FundraisingModel);
        }
        dispatch(getEntitiesAction(ENTITIES.fundraisings));
        return;
      }
      if (response.status === 400) {
        dispatch(setError({ [FORM.fundraising]: response.errors.fields }));
        dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.error }));
      }
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error saving fundraising',
      }));
    } catch (e) { /* Log the error here */
      Debug.errorLog(e);
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error saving fundraising',
      }));
    }
  };
